/**
 * Guard against open-redirect / redirect-based XSS through the `redirectTo`
 * query param that the member auth flow feeds into react-router's `navigate()`.
 *
 * react-router-dom 6.30.4 is affected by CVE-2026-53668 (open redirect leading
 * to XSS) and CVE-2026-53669 (backslash open-redirect bypass). There is no
 * within-v6 upstream fix (both require the 6->7 major, and 53668 is unpatched),
 * so we validate the destination ourselves before navigating.
 *
 * Only a same-origin, relative in-app path is allowed: it must start with a
 * single "/", must not be protocol-relative ("//..."), must not use a backslash
 * (the CVE bypass vector, e.g. "/\\evil.com" or "\\/\\/evil.com"), and must not
 * contain control characters. Anything with a scheme ("javascript:",
 * "https://evil") fails the leading-slash check. Legitimate redirect targets are
 * always built from `window.location.pathname`, so they already satisfy this.
 *
 * @returns the path when safe, otherwise null (caller should fall back to a
 *          known-good default destination).
 */
export function safeRedirectPath(
  raw: string | null | undefined,
): string | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  // Must be an absolute in-app path.
  if (!raw.startsWith("/")) return null;
  // Reject protocol-relative ("//host") which browsers treat as cross-origin.
  if (raw.startsWith("//")) return null;
  // Reject any backslash — the documented open-redirect bypass vector.
  if (raw.includes("\\")) return null;
  // Reject embedded control characters (newline/tab/NUL/etc.) used to smuggle
  // schemes or split the value.
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return null;
  }
  return raw;
}
